/**
 * Phase 10 — Backup & Restore page.
 *
 * Two cards:
 *   A. Download Full Backup — single button → triggers GET /api/backup/full.
 *   B. Restore from File   — file input → peek manifest → choose mode →
 *                            re-POST with mode/voyage_conflict and surface counts.
 *
 * UX safeguards:
 *   - "Replace all" requires typing the literal word REPLACE before the
 *     confirm button enables.
 *   - All operations show a spinner; full backups can take 10–30 seconds
 *     on a populated workspace.
 *   - Single-voyage uploads with a voyage_number collision show a
 *     resolver row (Replace / Create as new / Cancel).
 */
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Download, Upload, ShieldAlert, FileWarning, Database } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  buildFullBackupUrl,
  importBackup,
} from "@/lib/settingsApi";

const REPLACE_KEYWORD = "REPLACE";

function ResultBlock({ result }) {
  if (!result) return null;
  const imp = result.imported || {};
  const libRows = Object.entries(imp.libraries || {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <div
      className="rounded-md border bg-emerald-50 border-emerald-200 p-3 text-sm space-y-1"
      data-testid="backup-result"
    >
      <div className="font-medium text-emerald-900">
        Import complete ({result.type}{result.mode ? `, ${result.mode}` : ""}).
      </div>
      <ul className="text-xs text-emerald-800 list-disc pl-5">
        <li>Voyages: {imp.voyages ?? 0}</li>
        <li>Photos:  {imp.photos ?? 0}</li>
        <li>Settings: {imp.settings ? "yes" : "no"}</li>
        {libRows.length > 0 && (
          <li>
            Libraries:{" "}
            {libRows.map(([k, n]) => `${k.replace(/^lib_/, "")}: ${n}`).join(", ")}
          </li>
        )}
      </ul>
    </div>
  );
}

export default function BackupRestore() {
  const fileRef = useRef(null);

  // Section A — full backup download
  const [downloading, setDownloading] = useState(false);

  // Section B — restore
  const [file, setFile] = useState(null);
  const [peeking, setPeeking] = useState(false);
  const [manifest, setManifest] = useState(null);        // {type, ...}
  const [fullMode, setFullMode] = useState("merge");     // "replace" | "merge"
  const [replaceConfirm, setReplaceConfirm] = useState("");
  const [conflict, setConflict] = useState(null);        // {voyage_number, existing_id}
  const [voyageConflict, setVoyageConflict] = useState("new"); // "replace" | "new"
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadFull = () => {
    setDownloading(true);
    try {
      const url = buildFullBackupUrl();
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast({
        title: "Downloading full backup",
        description: "A ZIP containing voyages, libraries, settings, and photos.",
      });
    } catch (e) {
      toast({
        title: "Download failed",
        description: e?.message || "Could not start download",
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setDownloading(false), 1500);
    }
  };

  const resetRestoreState = () => {
    setManifest(null);
    setConflict(null);
    setResult(null);
    setReplaceConfirm("");
    setVoyageConflict("new");
    setFullMode("merge");
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0] || null;
    resetRestoreState();
    setFile(f);
    if (!f) return;
    setPeeking(true);
    try {
      const { status, data } = await importBackup(f, { peek: true });
      if (status >= 200 && status < 300) {
        setManifest(data);
      } else {
        toast({
          title: "Could not read file",
          description: data?.detail || `HTTP ${status}`,
          variant: "destructive",
        });
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast({
        title: "Could not read file",
        description: typeof detail === "string" ? detail : JSON.stringify(detail),
        variant: "destructive",
      });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } finally {
      setPeeking(false);
    }
  };

  const replaceLocked =
    manifest?.type === "full" &&
    fullMode === "replace" &&
    replaceConfirm.trim() !== REPLACE_KEYWORD;

  const handleRunImport = async () => {
    if (!file || !manifest) return;
    const opts = {};
    if (manifest.type === "full") opts.mode = fullMode;
    if (conflict) opts.voyage_conflict = voyageConflict;

    setImporting(true);
    setResult(null);
    try {
      const { status, data } = await importBackup(file, opts);
      if (status === 409 && data?.conflict) {
        setConflict(data.conflict);
        toast({
          title: "Voyage number conflict",
          description:
            "A voyage with this number already exists. Pick a resolution below and try again.",
        });
        return;
      }
      if (status < 200 || status >= 300) {
        toast({
          title: "Import failed",
          description: data?.detail || `HTTP ${status}`,
          variant: "destructive",
        });
        return;
      }
      setResult(data);
      const imp = data.imported || {};
      const partsList = [];
      if (imp.voyages) partsList.push(`${imp.voyages} voyage${imp.voyages !== 1 ? "s" : ""}`);
      const totalLibs = Object.values(imp.libraries || {}).reduce((a, b) => a + b, 0);
      if (totalLibs) partsList.push(`${totalLibs} library row${totalLibs !== 1 ? "s" : ""}`);
      if (imp.photos) partsList.push(`${imp.photos} photo${imp.photos !== 1 ? "s" : ""}`);
      toast({
        title: "Import complete",
        description: partsList.length
          ? `Imported ${partsList.join(", ")}.`
          : "Nothing new to import (everything was already present).",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast({
        title: "Import failed",
        description: typeof detail === "string" ? detail : JSON.stringify(detail),
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6 pb-24" data-testid="backup-restore-page">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Database className="h-6 w-6 text-primary" /> Backup &amp; Restore
        </h1>
        <p className="text-sm text-muted-foreground">
          Download a portable archive of your entire workspace, or restore from
          one. Use this to migrate between servers or roll back to a known
          good state.
        </p>
      </header>

      {/* Section A — full backup download */}
      <Card data-testid="section-download-full">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Download className="h-4 w-4" /> Download Full Backup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Contains all voyages, every library collection, the PDF layout
            settings, and every canapé photo on disk. Self-contained portable
            archive — restore it on any VoyageMenu server.
          </p>
          <Button
            onClick={handleDownloadFull}
            disabled={downloading}
            data-testid="download-full-backup-btn"
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download Full Backup
          </Button>
        </CardContent>
      </Card>

      {/* Section B — restore from file */}
      <Card data-testid="section-restore">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="h-4 w-4" /> Restore from File
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Accepts a single-voyage JSON, a voyage bundle ZIP, or a full
            backup ZIP. The file type is auto-detected.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="backup-file" className="text-sm font-medium">
              Backup file
            </Label>
            <Input
              ref={fileRef}
              id="backup-file"
              type="file"
              accept=".json,.zip,application/json,application/zip"
              onChange={handleFileChange}
              disabled={importing}
              data-testid="restore-file-input"
            />
          </div>

          {peeking && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Reading manifest…
            </div>
          )}

          {manifest && (
            <div
              className="rounded-md border bg-muted/40 p-3 text-sm space-y-1"
              data-testid="backup-manifest-preview"
            >
              <div className="font-medium">
                Detected: <span className="capitalize">{manifest.type}</span> backup
              </div>
              {manifest.type === "voyage" ? (
                <ul className="text-xs text-muted-foreground list-disc pl-5">
                  <li>Voyage number: {manifest.voyage_number || "?"}</li>
                  <li>Ship: {manifest.ship_name || "?"}</li>
                  <li>Days: {manifest.days ?? "?"}</li>
                </ul>
              ) : (
                <ul className="text-xs text-muted-foreground list-disc pl-5">
                  <li>Created: {manifest.manifest?.created_at || "?"}</li>
                  <li>Voyages: {manifest.voyages_count ?? 0}</li>
                  <li>Photos:  {manifest.photo_count ?? 0}</li>
                  <li>Settings included: {manifest.settings_included ? "yes" : "no"}</li>
                </ul>
              )}
            </div>
          )}

          {/* Full backup mode selector */}
          {manifest?.type === "full" && !conflict && (
            <div className="space-y-3" data-testid="full-mode-picker">
              <Label className="text-sm font-medium">Import mode</Label>
              <RadioGroup value={fullMode} onValueChange={setFullMode}>
                <div className="flex items-start gap-2">
                  <RadioGroupItem id="mode-merge" value="merge" data-testid="mode-merge-radio" />
                  <div>
                    <Label htmlFor="mode-merge" className="text-sm font-medium">
                      Merge (additive, safe)
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Keep everything currently in the database; only insert
                      rows from the backup that don't already exist.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem id="mode-replace" value="replace" data-testid="mode-replace-radio" />
                  <div>
                    <Label htmlFor="mode-replace" className="text-sm font-medium text-red-700">
                      Replace all existing data
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Wipe voyages + every library + PDF layout settings, then
                      restore from the backup. Photos already on disk are kept
                      if not in the backup (orphan-tolerant). Access token is
                      never touched.
                    </p>
                  </div>
                </div>
              </RadioGroup>
              {fullMode === "replace" && (
                <div
                  className="rounded-md border border-red-300 bg-red-50 p-3 space-y-2"
                  data-testid="replace-confirm-block"
                >
                  <div className="flex items-start gap-2 text-sm text-red-800">
                    <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      This permanently overwrites the current workspace. Type{" "}
                      <code className="font-mono font-semibold">REPLACE</code>{" "}
                      to enable the confirm button.
                    </span>
                  </div>
                  <Input
                    value={replaceConfirm}
                    onChange={(e) => setReplaceConfirm(e.target.value)}
                    placeholder="Type REPLACE to confirm"
                    data-testid="replace-confirm-input"
                  />
                </div>
              )}
            </div>
          )}

          {/* Voyage conflict resolver */}
          {conflict && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-3"
              data-testid="voyage-conflict-block"
            >
              <div className="flex items-start gap-2 text-sm text-amber-900">
                <FileWarning className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  A voyage already exists with the number{" "}
                  <strong>{conflict.voyage_number}</strong> (id{" "}
                  <code className="font-mono text-xs">{conflict.existing_id}</code>).
                  Pick how to proceed:
                </span>
              </div>
              <RadioGroup value={voyageConflict} onValueChange={setVoyageConflict}>
                <div className="flex items-start gap-2">
                  <RadioGroupItem id="vc-new" value="new" data-testid="vc-new-radio" />
                  <div>
                    <Label htmlFor="vc-new" className="text-sm font-medium">
                      Create as new voyage
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Keeps the existing voyage; inserts the incoming one with
                      a fresh id. The two will share the same voyage_number.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem id="vc-replace" value="replace" data-testid="vc-replace-radio" />
                  <div>
                    <Label htmlFor="vc-replace" className="text-sm font-medium text-red-700">
                      Replace existing voyage
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Deletes the existing voyage with this number and inserts
                      the incoming one with its original id.
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Run import */}
          {manifest && (
            <Button
              onClick={handleRunImport}
              disabled={importing || (manifest.type === "full" && replaceLocked)}
              data-testid="run-import-btn"
              variant={
                (manifest.type === "full" && fullMode === "replace") ||
                (conflict && voyageConflict === "replace")
                  ? "destructive"
                  : "default"
              }
            >
              {importing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {importing ? "Importing…" : "Run Import"}
            </Button>
          )}

          <ResultBlock result={result} />
        </CardContent>
      </Card>
    </div>
  );
}
